import Indicator from '../../../app/builder/condition/indicators/types/Indicator'

const num = (v: unknown, fallback: number): number =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

export type NodeCatalogEntry = {
  acceptsSeriesInput: boolean
  // UI warm-up estimate only; engines recompute authoritatively at evaluation
  // time. Factors mirror the charting engine's per-calculator minCandlesNeeded.
  warmup: (params: Record<string, any>) => number
}

// Standard indicators available as chain nodes. Deliberately excluded:
// averageTrueRangeBands (not registered in the TS charting engine) and
// pivotPoints (date-aware, incompatible with series evaluation). GTF
// indicators are excluded from chains entirely in v1.
// Formulas below are byte-for-byte mirrors of each engine calculator's
// minCandlesNeeded (greenchart-charting-engine, verified 2026-08-23) — the
// engine is the source of truth; drift here misprices def.minCandles, which
// engines use verbatim for buffer sizing.
export const NODE_CATALOG: Record<string, NodeCatalogEntry> = {
  simpleMovingAverage: { acceptsSeriesInput: true, warmup: (p) => num(p.period, 20) },
  exponentialMovingAverage: { acceptsSeriesInput: true, warmup: (p) => num(p.period, 20) * 4 },
  relativeStrengthIndex: { acceptsSeriesInput: true, warmup: (p) => num(p.period, 20) * 4 },
  bollingerBands: { acceptsSeriesInput: true, warmup: (p) => num(p.period, 20) },
  // Engine Stochastic.ts: minCandlesNeeded = period (its own smoothing warm-up
  // resolves as absent d values, not extra candles)
  stochastic: { acceptsSeriesInput: true, warmup: (p) => num(p.period, 14) },
  // NB: the registry value is 'atr', not 'averageTrueRange' — keys here MUST
  // match Indicator.value or the builder silently drops the indicator
  atr: { acceptsSeriesInput: false, warmup: (p) => num(p.period, 14) * 4 },
  movingAverageConvergenceDivergence: {
    acceptsSeriesInput: false,
    warmup: (p) =>
      Math.max(num(p.fastPeriod, 12), num(p.slowPeriod, 26), num(p.signalPeriod, 9)) * 4,
  },
  averageDirectionalIndex: { acceptsSeriesInput: false, warmup: (p) => num(p.period, 14) * 4 },
  commodityChannelIndex: { acceptsSeriesInput: false, warmup: (p) => num(p.period, 20) },
  williamsR: { acceptsSeriesInput: false, warmup: (p) => num(p.period, 14) },
  keltnerChannel: { acceptsSeriesInput: false, warmup: (p) => num(p.period, 20) },
  awesomeOscillator: {
    acceptsSeriesInput: false,
    warmup: (p) => Math.max(num(p.fastPeriod, 5), num(p.slowPeriod, 34)),
  },
  ichimokuCloud: {
    acceptsSeriesInput: false,
    warmup: (p) =>
      Math.max(
        num(p.conversionPeriod, 9),
        num(p.basePeriod, 26),
        num(p.spanPeriod, 52),
        num(p.displacement, 26)
      ),
  },
  movingAverageEnvelope: {
    acceptsSeriesInput: false,
    warmup: (p) => num(p.period, 20) * (p.movingAverageType === 'exponential' ? 4 : 1),
  },
  donchianChannel: {
    acceptsSeriesInput: false,
    warmup: (p) => Math.max(num(p.highPeriod, 20), num(p.lowPeriod, 20)),
  },
  parabolicSAR: { acceptsSeriesInput: false, warmup: () => 200 },
}

/** Output names for a chain node = the UI registry's fields minus Angle derivatives. */
export const nodeOutputs = (
  name: string,
  getIndicatorByValue: (value: string) => Indicator | undefined
): string[] => (getIndicatorByValue(name)?.fields ?? []).filter((f) => !f.endsWith('Angle'))
